// Folder/space tile-вид по образцу kanban-folder.jsx из прототипа.
// Карточки 280×180px с превью контента: для документов — первые строки,
// для пространств — мини-эскиз досок, для папок — иконки потомков.

import { Folder, FileText, Sparkles, FolderTree } from 'lucide-react';
import clsx from 'clsx';
import { api } from '../lib/api';

interface FolderViewProps {
  nodeId: string;
  title: string;
  onActivate: (id: string) => void;
}

export function FolderView({ nodeId, title, onActivate }: FolderViewProps) {
  const nodesQuery = api.treeNodes.list.useQuery();

  const allNodes = nodesQuery.data ?? [];
  const children = allNodes.filter((n) => n.parentId === nodeId);

  return (
    <main className="flex-1 overflow-auto p-8 bg-canvas-light dark:bg-canvas-dark">
      <header className="mb-6">
        <h1 className="text-xl font-semibold">{title}</h1>
        <p className="text-sm text-ink-faint_l dark:text-ink-faint_d mt-1">
          {children.length} {pluralize(children.length, 'элемент', 'элемента', 'элементов')}
        </p>
      </header>

      {children.length === 0 ? (
        <div className="text-center py-16 text-ink-faint_l dark:text-ink-faint_d text-sm">
          Папка пустая. Используй кнопку «+» в дереве, чтобы создать содержимое.
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {children.map((node) => (
            <TileCard
              key={node.id}
              node={node as TreeNodeMeta}
              allNodes={allNodes as TreeNodeMeta[]}
              onActivate={onActivate}
            />
          ))}
        </div>
      )}
    </main>
  );
}

interface TreeNodeMeta {
  id: string;
  parentId: string | null;
  kind: 'folder' | 'project' | 'space' | 'document';
  title: string | null;
  refId: string | null;
}

function TileCard({
  node,
  allNodes,
  onActivate,
}: {
  node: TreeNodeMeta;
  allNodes: TreeNodeMeta[];
  onActivate: (id: string) => void;
}) {
  const Icon = iconFor(node.kind);

  return (
    <button
      type="button"
      onClick={() => onActivate(node.id)}
      className={clsx(
        'rounded-lg border border-line-light dark:border-line-dark bg-surface-light dark:bg-surface-dark',
        'p-4 text-left hover:shadow-board-hover hover:border-accent/40 transition-all',
        'flex flex-col',
        'min-h-[200px]',
      )}
    >
      <div className="flex items-center gap-2 mb-3">
        <Icon size={16} className="text-accent" />
        <span className="text-[10px] uppercase tracking-wider text-ink-faint_l dark:text-ink-faint_d font-semibold">
          {labelOf(node.kind)}
        </span>
      </div>
      <h3 className="font-semibold text-[15px] mb-2 line-clamp-2">{node.title}</h3>

      {/* Превью контента в зависимости от типа */}
      <div className="flex-1 mt-2">
        {node.kind === 'folder' && <FolderPreview node={node} allNodes={allNodes} />}
        {node.kind === 'project' && <ProjectPreview node={node} />}
        {node.kind === 'space' && <SpacePreview spaceId={node.refId} />}
        {node.kind === 'document' && <DocumentPreview docId={node.refId} />}
      </div>
    </button>
  );
}

function FolderPreview({ node, allNodes }: { node: TreeNodeMeta; allNodes: TreeNodeMeta[] }) {
  const grandchildren = allNodes.filter((n) => n.parentId === node.id);
  const visible = grandchildren.slice(0, 4);
  return (
    <div className="grid grid-cols-2 gap-1.5">
      {visible.map((g) => {
        const Icon = iconFor(g.kind);
        return (
          <div
            key={g.id}
            className="rounded bg-surface2-light dark:bg-surface2-dark p-2 flex items-center gap-1.5 text-[11px]"
          >
            <Icon size={11} className="text-ink-muted_l dark:text-ink-muted_d shrink-0" />
            <span className="truncate text-ink-light dark:text-ink-dark">{g.title}</span>
          </div>
        );
      })}
      {visible.length === 0 && (
        <p className="col-span-2 text-xs text-ink-faint_l dark:text-ink-faint_d italic">пусто</p>
      )}
      {grandchildren.length > 4 && (
        <div className="col-span-2 text-[10px] text-ink-faint_l dark:text-ink-faint_d text-right">
          +{grandchildren.length - 4} ещё
        </div>
      )}
    </div>
  );
}

function ProjectPreview(_props: { node: TreeNodeMeta }) {
  return (
    <div className="text-xs text-ink-faint_l dark:text-ink-faint_d italic">
      Функционал проекта в разработке
    </div>
  );
}

function SpacePreview({ spaceId }: { spaceId: string | null }) {
  const spaceQuery = api.spaces.getWithBoards.useQuery(
    { spaceId: spaceId ?? '' },
    { enabled: !!spaceId },
  );
  const boards = spaceQuery.data?.boards ?? [];
  return (
    <div>
      {boards.length === 0 ? (
        <p className="text-xs text-ink-faint_l dark:text-ink-faint_d italic">
          Нет досок
        </p>
      ) : (
        <>
          {/* Мини-эскиз пары досок */}
          <div className="flex gap-1.5">
            {boards.slice(0, 3).map((b) => (
              <div
                key={b.id}
                className="flex-1 rounded bg-surface2-light dark:bg-surface2-dark p-1.5"
                title={b.title}
              >
                <div className="text-[10px] truncate text-ink-light dark:text-ink-dark font-medium mb-1">
                  {b.title}
                </div>
                <div className="flex gap-0.5">
                  <div className="flex-1 h-0.5 bg-gray-400/40 rounded" />
                  <div className="flex-1 h-0.5 bg-blue-500/40 rounded" />
                  <div className="flex-1 h-0.5 bg-purple-500/40 rounded" />
                  <div className="flex-1 h-0.5 bg-green-500/40 rounded" />
                </div>
              </div>
            ))}
          </div>
          <div className="text-[10px] text-ink-faint_l dark:text-ink-faint_d mt-1.5">
            {boards.length} {pluralize(boards.length, 'доска', 'доски', 'досок')}
          </div>
        </>
      )}
    </div>
  );
}

function DocumentPreview({ docId }: { docId: string | null }) {
  if (!docId) {
    return (
      <p className="text-xs text-ink-faint_l dark:text-ink-faint_d italic">пустой документ</p>
    );
  }
  return <DocumentPreviewInner docId={docId} />;
}

function DocumentPreviewInner({ docId }: { docId: string }) {
  const docQuery = api.documents.getFull.useQuery({ docId });
  const excerpt = docQuery.data?.document.excerpt ?? '';
  const wordCount = docQuery.data?.document.wordCount ?? 0;

  return (
    <div>
      <p className="text-xs text-ink-muted_l dark:text-ink-muted_d line-clamp-4 leading-relaxed">
        {excerpt || <span className="italic text-ink-faint_l dark:text-ink-faint_d">пустой документ</span>}
      </p>
      <div className="text-[10px] text-ink-faint_l dark:text-ink-faint_d mt-2">
        {wordCount} слов
      </div>
    </div>
  );
}

function iconFor(kind: 'folder' | 'project' | 'space' | 'document') {
  switch (kind) {
    case 'folder':
      return Folder;
    case 'project':
      return FolderTree;
    case 'space':
      return Sparkles;
    case 'document':
      return FileText;
  }
}

function labelOf(kind: 'folder' | 'project' | 'space' | 'document') {
  switch (kind) {
    case 'folder':
      return 'Папка';
    case 'project':
      return 'Проект';
    case 'space':
      return 'Пространство';
    case 'document':
      return 'Документ';
  }
}

function pluralize(n: number, one: string, few: string, many: string) {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return one;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return few;
  return many;
}
